
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCountry } from '@/contexts/country';
import { useGeolocation } from '@/components/pollster/hooks/useGeolocation';
import { useCountrySelection } from '@/hooks/useCountrySelection';
import { hasLaunchDatePassed, CAYMAN_LAUNCH_DATE } from '@/utils/date-constants';

export function useCountryRedirect(shouldRedirect: boolean = false) {
  const navigate = useNavigate();
  const location = useLocation();
  const { geoLocation, isLoading, error } = useGeolocation();
  const { availableCountries } = useCountry();
  const { selection } = useCountrySelection(false);

  useEffect(() => {
    // Skip redirect completely if not explicitly enabled
    if (!shouldRedirect || isLoading) {
      return;
    }

    // Log geolocation error if any
    if (error) {
      console.error('Geolocation error in redirect:', error);
      return;
    }

    // Skip redirect if no geolocation data
    if (!geoLocation?.countryCode) {
      console.warn('No country code available from geolocation');
      return;
    }
    
    // Get URL parameters
    const bypassWaitlist = location.search.includes('bypass-waitlist');
    const previewWaitlist = location.search.includes('preview-waitlist');
    const skipRedirect = location.search.includes('skip-redirect');

    // Check for a flag indicating we've already redirected once
    const hasInitialRedirectOccurred = sessionStorage.getItem('initial-redirect-occurred');
    
    // Skip redirect if we're not on homepage, redirect already occurred, or URL has skip-redirect parameter
    if (
      location.pathname !== '/' || 
      hasInitialRedirectOccurred === 'true' || 
      skipRedirect
    ) {
      return;
    }

    const { countryCode } = geoLocation;
    console.log(`Detected country code: ${countryCode}`);
    
    // Store last detected country in localStorage for tracking
    localStorage.setItem('last-detected-country', countryCode);
    
    // If user has already made a selection, use that instead, but only if redirect is explicitly enabled
    if (selection?.selectedCountry && shouldRedirect) {
      console.log(`Using saved country selection: ${selection.selectedCountry}`);
      
      // For country selections, proceed to the selected country's card page
      if (selection.selectedCountry !== 'global') {
        // NOTE: This is the only place in this hook where a redirect might happen,
        // but it should be disabled since we're passing shouldRedirect=false from CountryRedirectWrapper
        navigate(`/cards/${selection.selectedCountry}`, { replace: true });
        sessionStorage.setItem('initial-redirect-occurred', 'true');
      }
      return;
    }
    
    // No redirects by default - only if explicitly enabled
  }, [geoLocation, isLoading, error, navigate, location.pathname, location.search, availableCountries, shouldRedirect, selection]);

  return { geoLocation, isLoading, error };
}
